import {FC, useEffect, useContext} from "react";
import {useNavigate} from "react-router-dom";
import {LinearProgress} from "@mui/material";
import {useGetCourseDetails, useGetCourseCurriculum} from "@/app2/shared/data";
import {LocaleContext, resolveLocale} from "@/app2/shared/contexts";

interface CourseLandingPageProps {
	courseSlug: string;
}

/**
 * Course Landing Page
 * 
 * Auto-navigates to the next incomplete module in the course.
 * Shows loading spinner while determining navigation.
 */
export const CourseLandingPage: FC<CourseLandingPageProps> = ({courseSlug}) => {
	const navigate = useNavigate();

	const [{locale}] = useContext(LocaleContext);
	const detailsParams = {locale: resolveLocale(locale)};
	const curriculumParams = {locale: resolveLocale(locale)};

	// Fetch course details and curriculum
	const {data: courseData, isLoading: isLoadingCourse, error: courseError} = useGetCourseDetails(courseSlug, detailsParams);
	const {data: curriculumData, isLoading: isLoadingCurriculum, error: curriculumError} = useGetCourseCurriculum(courseSlug, curriculumParams, {
		enabled: !!courseData?.course?.enrollment
	});

	const course = courseData?.course;
	const curriculum = curriculumData;
	const isEnrolled = !!course?.enrollment;

	useEffect(() => {
		if (!curriculum || !isEnrolled) return;

		// Find first incomplete module
		const nextModule = curriculum.items.find(
			(item) => !item.complete && !item.isLocked
		);

		if (nextModule) {
			// Navigate to the next incomplete module
			navigate(
				`/course/${courseSlug}/${nextModule.type}/${nextModule.slug}`,
				{replace: true}
			);
		} else if (curriculum.progressPercent === 100) {
			// Course complete - navigate to first module so user can revisit content
			const firstModule = curriculum.items[0];
			if (firstModule) {
				navigate(
					`/course/${courseSlug}/${firstModule.type}/${firstModule.slug}`,
					{replace: true}
				);
			}
		} else {
			// All available modules complete but course not 100% - go to first module
			const firstModule = curriculum.items[0];
			if (firstModule) {
				navigate(
					`/course/${courseSlug}/${firstModule.type}/${firstModule.slug}`,
					{replace: true}
				);
			}
		}
	}, [curriculum, courseSlug, navigate, isEnrolled]);

	return <LinearProgress />;
};

